// ============================================================================
// COMPONENT ONTOLOGY - Defines all available components and their properties
// ============================================================================

import { ComponentDefinition, ComponentCategory, Protocol } from './types';

// Protocol compatibility matrix - which protocols can connect to which
export const PROTOCOL_COMPATIBILITY: Record<Protocol, string[]> = {
  http: ['compute', 'gateway', 'loadbalancer', 'cache.cdn'],
  https: ['compute', 'gateway', 'loadbalancer', 'cache.cdn', 'storage.object'],
  grpc: ['compute', 'gateway.grpc'],
  graphql: ['gateway.graphql', 'compute'],
  websocket: ['compute', 'gateway'],
  tcp: ['*'], // generic
  udp: ['dns', 'monitoring.metrics'],
  sql: ['db.relational', 'db.timeseries'],
  redis: ['cache.kv', 'db.keyvalue'],
  kafka: ['queue.stream', 'queue.pubsub'],
  amqp: ['queue.fifo', 'queue.pubsub', 'queue.task'],
  s3: ['storage.object'],
  smtp: ['compute'],
  dns: ['dns.authoritative', 'dns.resolver'],
  ssh: ['compute'],
  nfs: ['storage.file'],
  iscsi: ['storage.block'],
};

// Categories that can initiate connections
export const CAN_INITIATE: ComponentCategory[] = [
  'client',
  'compute',
  'gateway',
  'loadbalancer',
  'etl',
  'monitoring',
];

// Categories that can only receive connections (not initiate)
export const RECEIVE_ONLY: ComponentCategory[] = [
  'database',
  'cache',
  'queue',
  'storage',
];

// ============================================================================
// COMPONENT DEFINITIONS
// ============================================================================

export const COMPONENT_ONTOLOGY: ComponentDefinition[] = [
  // --------------------------------------------------------------------------
  // COMPUTE
  // --------------------------------------------------------------------------
  {
    id: 'ec2',
    name: 'EC2 Instance',
    category: 'compute',
    subtype: 'compute.vm',
    roles: ['server', 'data-plane'],
    ports: [
      { id: 'http-in', protocol: 'http', direction: 'in', required: false, description: 'HTTP server' },
      { id: 'https-in', protocol: 'https', direction: 'in', required: false, description: 'HTTPS server' },
      { id: 'http-out', protocol: 'http', direction: 'out', required: false, description: 'HTTP client' },
      { id: 'https-out', protocol: 'https', direction: 'out', required: false, description: 'HTTPS client' },
      { id: 'sql-out', protocol: 'sql', direction: 'out', required: false, description: 'SQL client' },
      { id: 'redis-out', protocol: 'redis', direction: 'out', required: false, description: 'Redis client' },
    ],
    constraints: {
      stateful: false,
      horizontallyScalable: true,
      supportsReplication: false,
      supportsSharding: false,
      maxTpsHeuristic: 10000,
      typicalLatencyMs: 10,
      requiresPersistentStorage: false,
      supportsMultiRegion: true,
      managed: false,
    },
    allowedAdjacency: [
      { targetSubtype: 'db.*', allowedProtocols: ['sql', 'redis', 'https'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
      { targetSubtype: 'cache.*', allowedProtocols: ['redis', 'https'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
      { targetSubtype: 'queue.*', allowedProtocols: ['kafka', 'amqp', 'https'], direction: 'outbound', allowedSemantics: ['async_event', 'async_task'], required: false },
    ],
    provider: 'aws',
    tags: ['compute', 'vm', 'aws'],
    costProfile: { monthlyBaseCost: 50, costPerMillionRequests: 0, costPerGbStored: 0, costPerGbTransfer: 0.09 },
  },
  {
    id: 'lambda',
    name: 'AWS Lambda',
    category: 'compute',
    subtype: 'compute.serverless',
    roles: ['server', 'data-plane'],
    ports: [
      { id: 'https-in', protocol: 'https', direction: 'in', required: true, description: 'HTTPS trigger' },
      { id: 'https-out', protocol: 'https', direction: 'out', required: false, description: 'HTTPS client' },
      { id: 'sql-out', protocol: 'sql', direction: 'out', required: false, description: 'SQL client' },
    ],
    constraints: {
      stateful: false,
      horizontallyScalable: true,
      supportsReplication: false,
      supportsSharding: false,
      maxTpsHeuristic: 100000,
      typicalLatencyMs: 100,
      requiresPersistentStorage: false,
      supportsMultiRegion: true,
      managed: true,
    },
    allowedAdjacency: [
      { targetSubtype: 'db.*', allowedProtocols: ['sql', 'https'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
      { targetSubtype: 'storage.object', allowedProtocols: ['s3'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
    ],
    provider: 'aws',
    tags: ['compute', 'serverless', 'aws', 'faas'],
    costProfile: { monthlyBaseCost: 0, costPerMillionRequests: 0.2, costPerGbStored: 0, costPerGbTransfer: 0 },
  },
  {
    id: 'ecs',
    name: 'ECS/Fargate',
    category: 'compute',
    subtype: 'compute.container',
    roles: ['server', 'data-plane'],
    ports: [
      { id: 'http-in', protocol: 'http', direction: 'in', required: false, description: 'HTTP server' },
      { id: 'https-in', protocol: 'https', direction: 'in', required: false, description: 'HTTPS server' },
      { id: 'grpc-in', protocol: 'grpc', direction: 'in', required: false, description: 'gRPC server' },
      { id: 'http-out', protocol: 'http', direction: 'out', required: false, description: 'HTTP client' },
      { id: 'sql-out', protocol: 'sql', direction: 'out', required: false, description: 'SQL client' },
    ],
    constraints: {
      stateful: false,
      horizontallyScalable: true,
      supportsReplication: false,
      supportsSharding: false,
      maxTpsHeuristic: 50000,
      typicalLatencyMs: 5,
      requiresPersistentStorage: false,
      supportsMultiRegion: true,
      managed: true,
    },
    allowedAdjacency: [
      { targetSubtype: 'db.*', allowedProtocols: ['sql', 'redis', 'https'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
      { targetSubtype: 'cache.*', allowedProtocols: ['redis'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
    ],
    provider: 'aws',
    tags: ['compute', 'container', 'aws', 'docker'],
    costProfile: { monthlyBaseCost: 30, costPerMillionRequests: 0, costPerGbStored: 0, costPerGbTransfer: 0.09 },
  },
  {
    id: 'gke',
    name: 'Google GKE',
    category: 'compute',
    subtype: 'compute.container',
    roles: ['server', 'data-plane'],
    ports: [
      { id: 'http-in', protocol: 'http', direction: 'in', required: false, description: 'HTTP server' },
      { id: 'https-in', protocol: 'https', direction: 'in', required: false, description: 'HTTPS server' },
      { id: 'grpc-in', protocol: 'grpc', direction: 'in', required: false, description: 'gRPC server' },
    ],
    constraints: {
      stateful: false,
      horizontallyScalable: true,
      supportsReplication: false,
      supportsSharding: false,
      maxTpsHeuristic: 100000,
      typicalLatencyMs: 5,
      requiresPersistentStorage: false,
      supportsMultiRegion: true,
      managed: true,
    },
    allowedAdjacency: [],
    provider: 'gcp',
    tags: ['compute', 'container', 'gcp', 'kubernetes'],
    costProfile: { monthlyBaseCost: 70, costPerMillionRequests: 0, costPerGbStored: 0, costPerGbTransfer: 0.12 },
  },
  {
    id: 'cloud-run',
    name: 'Cloud Run',
    category: 'compute',
    subtype: 'compute.serverless',
    roles: ['server', 'data-plane'],
    ports: [
      { id: 'https-in', protocol: 'https', direction: 'in', required: true, description: 'HTTPS trigger' },
    ],
    constraints: {
      stateful: false,
      horizontallyScalable: true,
      supportsReplication: false,
      supportsSharding: false,
      maxTpsHeuristic: 100000,
      typicalLatencyMs: 50,
      requiresPersistentStorage: false,
      supportsMultiRegion: true,
      managed: true,
    },
    allowedAdjacency: [],
    provider: 'gcp',
    tags: ['compute', 'serverless', 'gcp', 'container'],
    costProfile: { monthlyBaseCost: 0, costPerMillionRequests: 0.4, costPerGbStored: 0, costPerGbTransfer: 0 },
  },

  // --------------------------------------------------------------------------
  // DATABASE
  // --------------------------------------------------------------------------
  {
    id: 'postgresql',
    name: 'PostgreSQL',
    category: 'database',
    subtype: 'db.relational',
    roles: ['storage'],
    ports: [
      { id: 'sql-in', protocol: 'sql', direction: 'in', required: true, description: 'SQL queries' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: false,
      supportsReplication: true,
      supportsSharding: false,
      maxTpsHeuristic: 10000,
      typicalLatencyMs: 5,
      requiresPersistentStorage: true,
      supportsMultiRegion: true,
      managed: false,
    },
    allowedAdjacency: [
      { targetSubtype: 'db.relational', allowedProtocols: ['sql'], direction: 'outbound', allowedSemantics: ['replication'], required: false },
    ],
    provider: 'open_source',
    tags: ['database', 'relational', 'sql', 'acid'],
    costProfile: { monthlyBaseCost: 100, costPerMillionRequests: 0, costPerGbStored: 0.1, costPerGbTransfer: 0 },
  },
  {
    id: 'aurora',
    name: 'Amazon Aurora',
    category: 'database',
    subtype: 'db.relational',
    roles: ['storage'],
    ports: [
      { id: 'sql-in', protocol: 'sql', direction: 'in', required: true, description: 'SQL queries' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: true,
      supportsReplication: true,
      supportsSharding: false,
      maxTpsHeuristic: 100000,
      typicalLatencyMs: 3,
      requiresPersistentStorage: true,
      supportsMultiRegion: true,
      managed: true,
    },
    allowedAdjacency: [],
    provider: 'aws',
    tags: ['database', 'relational', 'sql', 'aws', 'managed'],
    costProfile: { monthlyBaseCost: 200, costPerMillionRequests: 0.2, costPerGbStored: 0.1, costPerGbTransfer: 0 },
  },
  {
    id: 'mysql',
    name: 'MySQL',
    category: 'database',
    subtype: 'db.relational',
    roles: ['storage'],
    ports: [
      { id: 'sql-in', protocol: 'sql', direction: 'in', required: true, description: 'SQL queries' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: false,
      supportsReplication: true,
      supportsSharding: false,
      maxTpsHeuristic: 8000,
      typicalLatencyMs: 5,
      requiresPersistentStorage: true,
      supportsMultiRegion: true,
      managed: false,
    },
    allowedAdjacency: [],
    provider: 'open_source',
    tags: ['database', 'relational', 'sql'],
    costProfile: { monthlyBaseCost: 80, costPerMillionRequests: 0, costPerGbStored: 0.1, costPerGbTransfer: 0 },
  },
  {
    id: 'mongodb',
    name: 'MongoDB',
    category: 'database',
    subtype: 'db.document',
    roles: ['storage'],
    ports: [
      { id: 'mongo-in', protocol: 'tcp', direction: 'in', required: true, description: 'MongoDB protocol' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: true,
      supportsReplication: true,
      supportsSharding: true,
      maxTpsHeuristic: 50000,
      typicalLatencyMs: 3,
      requiresPersistentStorage: true,
      supportsMultiRegion: true,
      managed: false,
    },
    allowedAdjacency: [],
    provider: 'open_source',
    tags: ['database', 'document', 'nosql'],
    costProfile: { monthlyBaseCost: 100, costPerMillionRequests: 0, costPerGbStored: 0.25, costPerGbTransfer: 0 },
  },
  {
    id: 'dynamodb',
    name: 'DynamoDB',
    category: 'database',
    subtype: 'db.document',
    roles: ['storage'],
    ports: [
      { id: 'https-in', protocol: 'https', direction: 'in', required: true, description: 'DynamoDB API' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: true,
      supportsReplication: true,
      supportsSharding: true,
      maxTpsHeuristic: 1000000,
      typicalLatencyMs: 5,
      requiresPersistentStorage: true,
      supportsMultiRegion: true,
      managed: true,
    },
    allowedAdjacency: [],
    provider: 'aws',
    tags: ['database', 'document', 'nosql', 'aws', 'serverless'],
    costProfile: { monthlyBaseCost: 0, costPerMillionRequests: 1.25, costPerGbStored: 0.25, costPerGbTransfer: 0 },
  },

  // --------------------------------------------------------------------------
  // CACHE
  // --------------------------------------------------------------------------
  {
    id: 'redis',
    name: 'Redis',
    category: 'cache',
    subtype: 'cache.kv',
    roles: ['storage', 'broker'],
    ports: [
      { id: 'redis-in', protocol: 'redis', direction: 'in', required: true, description: 'Redis protocol' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: true,
      supportsReplication: true,
      supportsSharding: true,
      maxTpsHeuristic: 100000,
      typicalLatencyMs: 1,
      requiresPersistentStorage: false,
      supportsMultiRegion: true,
      managed: false,
    },
    allowedAdjacency: [],
    provider: 'open_source',
    tags: ['cache', 'key-value', 'pubsub'],
    costProfile: { monthlyBaseCost: 50, costPerMillionRequests: 0, costPerGbStored: 0, costPerGbTransfer: 0 },
  },
  {
    id: 'elasticache',
    name: 'ElastiCache',
    category: 'cache',
    subtype: 'cache.kv',
    roles: ['storage'],
    ports: [
      { id: 'redis-in', protocol: 'redis', direction: 'in', required: true, description: 'Redis protocol' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: true,
      supportsReplication: true,
      supportsSharding: true,
      maxTpsHeuristic: 200000,
      typicalLatencyMs: 0.5,
      requiresPersistentStorage: false,
      supportsMultiRegion: true,
      managed: true,
    },
    allowedAdjacency: [],
    provider: 'aws',
    tags: ['cache', 'key-value', 'aws', 'managed'],
    costProfile: { monthlyBaseCost: 100, costPerMillionRequests: 0, costPerGbStored: 0, costPerGbTransfer: 0 },
  },
  {
    id: 'cloudfront',
    name: 'CloudFront',
    category: 'cache',
    subtype: 'cache.cdn',
    roles: ['proxy'],
    ports: [
      { id: 'https-in', protocol: 'https', direction: 'in', required: true, description: 'HTTPS from clients' },
      { id: 'https-out', protocol: 'https', direction: 'out', required: true, description: 'HTTPS to origin' },
    ],
    constraints: {
      stateful: false,
      horizontallyScalable: true,
      supportsReplication: false,
      supportsSharding: false,
      maxTpsHeuristic: 10000000,
      typicalLatencyMs: 10,
      requiresPersistentStorage: false,
      supportsMultiRegion: true,
      managed: true,
    },
    allowedAdjacency: [
      { targetSubtype: 'storage.object', allowedProtocols: ['https', 's3'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
      { targetSubtype: 'compute.*', allowedProtocols: ['https'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
      { targetSubtype: 'lb.*', allowedProtocols: ['https'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
    ],
    provider: 'aws',
    tags: ['cache', 'cdn', 'aws', 'edge'],
    costProfile: { monthlyBaseCost: 0, costPerMillionRequests: 0.01, costPerGbStored: 0, costPerGbTransfer: 0.085 },
  },

  // --------------------------------------------------------------------------
  // QUEUE
  // --------------------------------------------------------------------------
  {
    id: 'sqs',
    name: 'Amazon SQS',
    category: 'queue',
    subtype: 'queue.fifo',
    roles: ['broker'],
    ports: [
      { id: 'https-in', protocol: 'https', direction: 'in', required: true, description: 'SQS API' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: true,
      supportsReplication: true,
      supportsSharding: false,
      maxTpsHeuristic: 30000,
      typicalLatencyMs: 20,
      requiresPersistentStorage: true,
      supportsMultiRegion: false,
      managed: true,
    },
    allowedAdjacency: [],
    provider: 'aws',
    tags: ['queue', 'messaging', 'aws', 'fifo'],
    costProfile: { monthlyBaseCost: 0, costPerMillionRequests: 0.4, costPerGbStored: 0, costPerGbTransfer: 0 },
  },
  {
    id: 'kafka',
    name: 'Apache Kafka',
    category: 'queue',
    subtype: 'queue.stream',
    roles: ['broker'],
    ports: [
      { id: 'kafka-in', protocol: 'kafka', direction: 'in', required: true, description: 'Kafka protocol' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: true,
      supportsReplication: true,
      supportsSharding: true,
      maxTpsHeuristic: 1000000,
      typicalLatencyMs: 5,
      requiresPersistentStorage: true,
      supportsMultiRegion: true,
      managed: false,
    },
    allowedAdjacency: [],
    provider: 'open_source',
    tags: ['queue', 'streaming', 'event-driven'],
    costProfile: { monthlyBaseCost: 200, costPerMillionRequests: 0, costPerGbStored: 0.1, costPerGbTransfer: 0 },
  },
  {
    id: 'rabbitmq',
    name: 'RabbitMQ',
    category: 'queue',
    subtype: 'queue.pubsub',
    roles: ['broker'],
    ports: [
      { id: 'amqp-in', protocol: 'amqp', direction: 'in', required: true, description: 'AMQP protocol' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: true,
      supportsReplication: true,
      supportsSharding: false,
      maxTpsHeuristic: 50000,
      typicalLatencyMs: 2,
      requiresPersistentStorage: true,
      supportsMultiRegion: false,
      managed: false,
    },
    allowedAdjacency: [],
    provider: 'open_source',
    tags: ['queue', 'messaging', 'amqp'],
    costProfile: { monthlyBaseCost: 50, costPerMillionRequests: 0, costPerGbStored: 0, costPerGbTransfer: 0 },
  },

  // --------------------------------------------------------------------------
  // GATEWAY & LOAD BALANCER
  // --------------------------------------------------------------------------
  {
    id: 'api-gateway',
    name: 'API Gateway',
    category: 'gateway',
    subtype: 'gateway.api',
    roles: ['proxy', 'control-plane'],
    ports: [
      { id: 'https-in', protocol: 'https', direction: 'in', required: true, description: 'HTTPS from clients' },
      { id: 'https-out', protocol: 'https', direction: 'out', required: true, description: 'HTTPS to backends' },
    ],
    constraints: {
      stateful: false,
      horizontallyScalable: true,
      supportsReplication: false,
      supportsSharding: false,
      maxTpsHeuristic: 100000,
      typicalLatencyMs: 20,
      requiresPersistentStorage: false,
      supportsMultiRegion: true,
      managed: true,
    },
    allowedAdjacency: [
      { targetSubtype: 'compute.*', allowedProtocols: ['https', 'http'], direction: 'outbound', allowedSemantics: ['request_response'], required: true },
      { targetSubtype: 'lb.*', allowedProtocols: ['https', 'http'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
    ],
    provider: 'aws',
    tags: ['gateway', 'api', 'aws', 'managed'],
    costProfile: { monthlyBaseCost: 0, costPerMillionRequests: 3.5, costPerGbStored: 0, costPerGbTransfer: 0.09 },
  },
  {
    id: 'alb',
    name: 'Application Load Balancer',
    category: 'loadbalancer',
    subtype: 'lb.l7',
    roles: ['proxy'],
    ports: [
      { id: 'https-in', protocol: 'https', direction: 'in', required: false, description: 'HTTPS from clients' },
      { id: 'http-in', protocol: 'http', direction: 'in', required: false, description: 'HTTP from clients' },
      { id: 'https-out', protocol: 'https', direction: 'out', required: false, description: 'HTTPS to backends' },
      { id: 'http-out', protocol: 'http', direction: 'out', required: false, description: 'HTTP to backends' },
    ],
    constraints: {
      stateful: false,
      horizontallyScalable: true,
      supportsReplication: false,
      supportsSharding: false,
      maxTpsHeuristic: 1000000,
      typicalLatencyMs: 5,
      requiresPersistentStorage: false,
      supportsMultiRegion: false,
      managed: true,
    },
    allowedAdjacency: [
      { targetSubtype: 'compute.*', allowedProtocols: ['https', 'http'], direction: 'outbound', allowedSemantics: ['request_response'], required: true },
    ],
    provider: 'aws',
    tags: ['loadbalancer', 'l7', 'aws'],
    costProfile: { monthlyBaseCost: 20, costPerMillionRequests: 0, costPerGbStored: 0, costPerGbTransfer: 0.008 },
  },
  {
    id: 'nlb',
    name: 'Network Load Balancer',
    category: 'loadbalancer',
    subtype: 'lb.l4',
    roles: ['proxy'],
    ports: [
      { id: 'tcp-in', protocol: 'tcp', direction: 'in', required: true, description: 'TCP from clients' },
      { id: 'tcp-out', protocol: 'tcp', direction: 'out', required: true, description: 'TCP to backends' },
    ],
    constraints: {
      stateful: false,
      horizontallyScalable: true,
      supportsReplication: false,
      supportsSharding: false,
      maxTpsHeuristic: 10000000,
      typicalLatencyMs: 1,
      requiresPersistentStorage: false,
      supportsMultiRegion: false,
      managed: true,
    },
    allowedAdjacency: [],
    provider: 'aws',
    tags: ['loadbalancer', 'l4', 'aws', 'tcp'],
    costProfile: { monthlyBaseCost: 20, costPerMillionRequests: 0, costPerGbStored: 0, costPerGbTransfer: 0.006 },
  },

  // --------------------------------------------------------------------------
  // STORAGE
  // --------------------------------------------------------------------------
  {
    id: 's3',
    name: 'Amazon S3',
    category: 'storage',
    subtype: 'storage.object',
    roles: ['storage'],
    ports: [
      { id: 's3-in', protocol: 's3', direction: 'in', required: true, description: 'S3 API' },
      { id: 'https-in', protocol: 'https', direction: 'in', required: false, description: 'HTTPS API' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: true,
      supportsReplication: true,
      supportsSharding: true,
      maxTpsHeuristic: 100000,
      typicalLatencyMs: 50,
      requiresPersistentStorage: true,
      supportsMultiRegion: true,
      managed: true,
    },
    allowedAdjacency: [],
    provider: 'aws',
    tags: ['storage', 'object', 'aws', 's3'],
    costProfile: { monthlyBaseCost: 0, costPerMillionRequests: 0.005, costPerGbStored: 0.023, costPerGbTransfer: 0.09 },
  },

  // --------------------------------------------------------------------------
  // DNS
  // --------------------------------------------------------------------------
  {
    id: 'route53',
    name: 'Route 53',
    category: 'dns',
    subtype: 'dns.authoritative',
    roles: ['control-plane'],
    ports: [
      { id: 'dns-in', protocol: 'dns', direction: 'in', required: true, description: 'DNS queries' },
    ],
    constraints: {
      stateful: false,
      horizontallyScalable: true,
      supportsReplication: true,
      supportsSharding: false,
      maxTpsHeuristic: 10000000,
      typicalLatencyMs: 20,
      requiresPersistentStorage: false,
      supportsMultiRegion: true,
      managed: true,
    },
    allowedAdjacency: [],
    provider: 'aws',
    tags: ['dns', 'aws', 'global'],
    costProfile: { monthlyBaseCost: 0.5, costPerMillionRequests: 0.4, costPerGbStored: 0, costPerGbTransfer: 0 },
  },

  // --------------------------------------------------------------------------
  // SECURITY
  // --------------------------------------------------------------------------
  {
    id: 'waf',
    name: 'AWS WAF',
    category: 'security',
    subtype: 'security.waf',
    roles: ['proxy'],
    ports: [
      { id: 'https-in', protocol: 'https', direction: 'in', required: true, description: 'HTTPS traffic' },
      { id: 'https-out', protocol: 'https', direction: 'out', required: true, description: 'HTTPS to origin' },
    ],
    constraints: {
      stateful: false,
      horizontallyScalable: true,
      supportsReplication: false,
      supportsSharding: false,
      maxTpsHeuristic: 10000000,
      typicalLatencyMs: 2,
      requiresPersistentStorage: false,
      supportsMultiRegion: true,
      managed: true,
    },
    allowedAdjacency: [
      { targetSubtype: 'gateway.*', allowedProtocols: ['https'], direction: 'outbound', allowedSemantics: ['request_response'], required: true },
      { targetSubtype: 'lb.*', allowedProtocols: ['https'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
    ],
    provider: 'aws',
    tags: ['security', 'waf', 'aws', 'firewall'],
    costProfile: { monthlyBaseCost: 5, costPerMillionRequests: 0.6, costPerGbStored: 0, costPerGbTransfer: 0 },
  },
  {
    id: 'cognito',
    name: 'Amazon Cognito',
    category: 'security',
    subtype: 'security.identity',
    roles: ['control-plane'],
    ports: [
      { id: 'https-in', protocol: 'https', direction: 'in', required: true, description: 'Auth API' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: true,
      supportsReplication: true,
      supportsSharding: false,
      maxTpsHeuristic: 100000,
      typicalLatencyMs: 50,
      requiresPersistentStorage: true,
      supportsMultiRegion: true,
      managed: true,
    },
    allowedAdjacency: [],
    provider: 'aws',
    tags: ['security', 'identity', 'auth', 'aws'],
    costProfile: { monthlyBaseCost: 0, costPerMillionRequests: 0, costPerGbStored: 0, costPerGbTransfer: 0 },
  },

  // --------------------------------------------------------------------------
  // MONITORING
  // --------------------------------------------------------------------------
  {
    id: 'prometheus',
    name: 'Prometheus',
    category: 'monitoring',
    subtype: 'monitoring.metrics',
    roles: ['control-plane'],
    ports: [
      { id: 'http-in', protocol: 'http', direction: 'in', required: false, description: 'Query API' },
      { id: 'http-out', protocol: 'http', direction: 'out', required: true, description: 'Scrape targets' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: false,
      supportsReplication: true,
      supportsSharding: false,
      maxTpsHeuristic: 10000,
      typicalLatencyMs: 10,
      requiresPersistentStorage: true,
      supportsMultiRegion: false,
      managed: false,
    },
    allowedAdjacency: [],
    provider: 'open_source',
    tags: ['monitoring', 'metrics', 'observability'],
    costProfile: { monthlyBaseCost: 30, costPerMillionRequests: 0, costPerGbStored: 0.1, costPerGbTransfer: 0 },
  },
  {
    id: 'elasticsearch',
    name: 'Elasticsearch',
    category: 'monitoring',
    subtype: 'monitoring.logging',
    roles: ['storage'],
    ports: [
      { id: 'https-in', protocol: 'https', direction: 'in', required: true, description: 'Search API' },
    ],
    constraints: {
      stateful: true,
      horizontallyScalable: true,
      supportsReplication: true,
      supportsSharding: true,
      maxTpsHeuristic: 50000,
      typicalLatencyMs: 20,
      requiresPersistentStorage: true,
      supportsMultiRegion: false,
      managed: false,
    },
    allowedAdjacency: [],
    provider: 'open_source',
    tags: ['monitoring', 'logging', 'search', 'elk'],
    costProfile: { monthlyBaseCost: 100, costPerMillionRequests: 0, costPerGbStored: 0.1, costPerGbTransfer: 0 },
  },

  // --------------------------------------------------------------------------
  // CLIENT
  // --------------------------------------------------------------------------
  {
    id: 'web-client',
    name: 'Web Browser',
    category: 'client',
    subtype: 'client.web',
    roles: ['client'],
    ports: [
      { id: 'https-out', protocol: 'https', direction: 'out', required: true, description: 'HTTPS requests' },
      { id: 'websocket-out', protocol: 'websocket', direction: 'out', required: false, description: 'WebSocket' },
    ],
    constraints: {
      stateful: false,
      horizontallyScalable: false,
      supportsReplication: false,
      supportsSharding: false,
      maxTpsHeuristic: 100,
      typicalLatencyMs: 0,
      requiresPersistentStorage: false,
      supportsMultiRegion: false,
      managed: false,
    },
    allowedAdjacency: [
      { targetSubtype: 'cache.cdn', allowedProtocols: ['https'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
      { targetSubtype: 'gateway.*', allowedProtocols: ['https'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
      { targetSubtype: 'lb.*', allowedProtocols: ['https'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
      { targetSubtype: 'security.waf', allowedProtocols: ['https'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
    ],
    provider: 'generic',
    tags: ['client', 'browser', 'frontend'],
    costProfile: { monthlyBaseCost: 0, costPerMillionRequests: 0, costPerGbStored: 0, costPerGbTransfer: 0 },
  },
  {
    id: 'mobile-client',
    name: 'Mobile App',
    category: 'client',
    subtype: 'client.mobile',
    roles: ['client'],
    ports: [
      { id: 'https-out', protocol: 'https', direction: 'out', required: true, description: 'HTTPS requests' },
    ],
    constraints: {
      stateful: false,
      horizontallyScalable: false,
      supportsReplication: false,
      supportsSharding: false,
      maxTpsHeuristic: 10,
      typicalLatencyMs: 0,
      requiresPersistentStorage: false,
      supportsMultiRegion: false,
      managed: false,
    },
    allowedAdjacency: [
      { targetSubtype: 'gateway.*', allowedProtocols: ['https'], direction: 'outbound', allowedSemantics: ['request_response'], required: false },
    ],
    provider: 'generic',
    tags: ['client', 'mobile', 'app'],
    costProfile: { monthlyBaseCost: 0, costPerMillionRequests: 0, costPerGbStored: 0, costPerGbTransfer: 0 },
  },
];

// Helper to get component by ID
export function getComponentById(id: string): ComponentDefinition | undefined {
  return COMPONENT_ONTOLOGY.find(c => c.id === id);
}

// Helper to get components by category
export function getComponentsByCategory(category: ComponentCategory): ComponentDefinition[] {
  return COMPONENT_ONTOLOGY.filter(c => c.category === category);
}

// Helper to get components by subtype pattern (supports wildcards)
export function getComponentsBySubtype(pattern: string): ComponentDefinition[] {
  if (pattern.includes('*')) {
    const prefix = pattern.replace('.*', '').replace('*', '');
    return COMPONENT_ONTOLOGY.filter(c => c.subtype.startsWith(prefix));
  }
  return COMPONENT_ONTOLOGY.filter(c => c.subtype === pattern);
}

// Check if a protocol is valid for a target component
export function isProtocolValidForTarget(protocol: Protocol, targetComponent: ComponentDefinition): boolean {
  const validTargets = PROTOCOL_COMPATIBILITY[protocol];
  if (validTargets.includes('*')) return true;
  return validTargets.some(target =>
    target === targetComponent.category ||
    target === targetComponent.subtype
  );
}
